/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { FC } from 'react'
import { useIntl } from 'react-intl'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiIcon,
  EuiSuperSelect,
  EuiToolTip,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { withErrorBoundary } from '@modules/cui/Boundary'
import type { PlatformId } from '@modules/ui-types'
import { getPlatformInfoById } from '@modules/utils/platform'
import type { PlatformInfo } from '@modules/utils/platform'
import { useProfile } from '@modules/profile-lib/hooks'

interface Props {
  platform: PlatformId
  availablePlatforms: PlatformId[]
  onChange: (platform: string) => void
  restoreFromSnapshot: boolean
  disabled?: boolean
  prepend?: boolean
  'aria-labelledby'?: string
  isPricingPage?: boolean
}

const SelectCloudPlatform: FC<Props> = ({
  platform,
  availablePlatforms,
  onChange,
  restoreFromSnapshot,
  disabled,
  prepend = true,
  isPricingPage = false,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const { formatMessage } = useIntl()
  const profile = useProfile({ enabled: !isPricingPage })

  const label = formatMessage({
    id: 'select-platform-label',
    defaultMessage: 'Cloud provider',
  })

  const marketplaceDisabledProvider =
    profile?.domain === 'gcp' || profile?.domain === 'azure' || profile?.domain === 'aws'

  const platformOptions =
    availablePlatforms &&
    availablePlatforms
      .map((key) => getPlatformInfoById(key))
      .map((platform) => ({
        inputDisplay: renderPlatformOption(platform),
        value: platform.id,
        'data-test-id': `cloud-platform-${platform.id}`,
      }))

  const select = (
    <EuiSuperSelect
      fullWidth={true}
      popoverProps={{ css: { '.euiFormControlLayout__prepend': { width: 180 } } }}
      prepend={prepend ? label : undefined}
      options={platformOptions}
      valueOfSelected={platform}
      onChange={(platform) => onChange(platform)}
      disabled={restoreFromSnapshot || disabled || marketplaceDisabledProvider}
      data-test-id='platform-combobox'
      aria-label={ariaLabelledBy ?? label}
    />
  )

  return restoreFromSnapshot ? (
    <EuiToolTip
      display='block'
      position='top'
      content={
        <FormattedMessage
          defaultMessage='You cannot change the cloud provider as you are restoring data from a snapshot.'
          id='select-cloud-provider-disabled-tooltip'
        />
      }
    >
      {select}
    </EuiToolTip>
  ) : (
    select
  )
}

const renderPlatformOption = (platform: PlatformInfo) => (
  <EuiFlexGroup
    data-test-id={`cloud-platform-option-${platform.id}`}
    gutterSize='s'
    alignItems='center'
    responsive={false}
  >
    <EuiFlexItem grow={false}>
      <EuiIcon size='m' type={platform.iconType} />
    </EuiFlexItem>

    <EuiFlexItem>
      <EuiText style={{ paddingLeft: 0 }} size='s'>
        {platform.title}
      </EuiText>
    </EuiFlexItem>
  </EuiFlexGroup>
)

export default withErrorBoundary(SelectCloudPlatform)
